/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import AssetScaler from '../AssetScaler';
import SliderFill from '../UI/SliderFill';
import MutatorComponentBase from './MutatorComponentBase';
import ClassUtil from '../../ClassUtil';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default class AssetScalarMutator extends MutatorComponentBase
{
    @property(cc.Node)
    protected top: cc.Node = null;

    @property()
    public get ConfigureAssetScaling(): boolean { return false; }
    public set ConfigureAssetScaling(value: boolean)
    {
        this.execute(this.top, [this.labelHandler.bind(this), this.spriteHandler.bind(this)]);
    }

    @property()
    public get ConfigureLabelsOnly(): boolean { return false; }
    public set ConfigureLabelsOnly(value: boolean)
    {
        this.execute(this.top, [this.labelHandler.bind(this)]);
    }

    @property()
    public get RemoveAssetScalars(): boolean { return false; }
    public set RemoveAssetScalars(value: boolean)
    {
        this.execute(this.top, [this.removeAssetScalar.bind(this)]);
    }

    // #region AssetScalar Configuration Helpers

    private labelHandler(target: cc.Node): boolean
    {
        let label: cc.Label = target.getComponent(cc.Label);

        if (label && label.font)
        {
            return this.addAssetScalar(target, 'Label with font.', true);
        }

        return false;
    }

    private spriteHandler(target: cc.Node): boolean
    {
        let sprite: cc.Sprite = target.getComponent(cc.Sprite);
        let success: boolean = false;

        if (!sprite) { return false; }

        if (sprite.sizeMode === cc.Sprite.SizeMode.TRIMMED)
        {
            this.warn(target, `SizeMode set to ${cc.Sprite.SizeMode[sprite.sizeMode]}.`);
            if (this.shouldModify)
            {
                sprite.sizeMode = cc.Sprite.SizeMode.CUSTOM;
                this.warn(target, `FIXED - Size mode set to ${cc.Sprite.SizeMode[sprite.sizeMode]}`);
            }
            success = true;
        }

        if (sprite.type === cc.Sprite.Type.TILED)
        {
            success = this.addAssetScalar(target, `Sprite is ${cc.Sprite.Type[sprite.type]}`)
                        || success;
        }

        if (sprite.type === cc.Sprite.Type.SLICED)
        {
            let spriteFrame: cc.SpriteFrame = sprite.spriteFrame;

            if (spriteFrame)
            {
                let border: number
                    = sprite.spriteFrame.insetTop
                        + sprite.spriteFrame.insetBottom
                        + sprite.spriteFrame.insetLeft
                        + sprite.spriteFrame.insetRight;

                if (border <= 0)
                {
                    this.warn(target, `Sprite is ${cc.Sprite.Type[sprite.type]}, but has no border.`);
                    if (this.shouldModify)
                    {
                        sprite.type = cc.Sprite.Type.SIMPLE;
                        this.warn(target, `FIXED - Type set to ${cc.Sprite.Type[sprite.type]}.`);
                    }
                    success = true;
                }
                else
                {
                    success = this.addAssetScalar(target, `Sprite is ${cc.Sprite.Type[sprite.type]}.`)
                                || success;
                }
            }
            else
            {
                this.error(target, 'Sprite has no sprite frame.');
            }
        }

        return success;
    }

    private addAssetScalar(target: cc.Node, extendedMessage = '', alwaysFix: boolean = false): boolean
    {
        let currentAssetScalars: AssetScaler[] = target.getComponents(AssetScaler);

        let unWantedComponents: string[] = [];

        if (target.getComponent(cc.Scrollbar)) { unWantedComponents.push(cc.Scrollbar.name); }
        if (target.getComponent(SliderFill)) { unWantedComponents.push(ClassUtil.getName(SliderFill)); }
        if (target.getComponent(cc.Button)) { unWantedComponents.push(cc.Button.name); }
        if (target.getComponent(cc.Layout)) { unWantedComponents.push(cc.Layout.name); }

        let unWantedString: string = unWantedComponents.length > 0
            ? `Includes: ${unWantedComponents.join(' | ')}.`
            : '';

        if (!currentAssetScalars || currentAssetScalars.length === 0)
        {
            let message: string = `${AssetScaler.name} missing. ${extendedMessage}`;
            let shouldFix: boolean = this.shouldModify;

            if (!alwaysFix)
            {
                if (target.children && target.children.length > 0)
                {
                    this.log(target, `${message} HAS CHILDREN. ${unWantedString}`);
                    shouldFix = false;
                }
                else if (unWantedString)
                {
                    this.log(target, `${message} ${unWantedString}`);
                    shouldFix = false;
                }
                else
                {
                    this.warn(target, message);
                }
            }
            else
            {
                this.warn(target, message);
            }

            if (shouldFix || alwaysFix)
            {
                target.addComponent(AssetScaler);
                this.warn(target, `FIXED - ${AssetScaler.name} added.`);
            }
            return true;
        }
        else if (currentAssetScalars.length > 1)
        {
            let count: number = currentAssetScalars.length - 1;

            this.warn(target, `Found ${count + 1} ${AssetScaler.name}'s`);

            if (this.shouldModify)
            {
                while (count)
                {
                    currentAssetScalars[count].destroy();

                    count--;
                }
                this.warn(target, `FIXED - ${AssetScaler.name} removed, solo uno.`);
            }

            return true;
        }

        return false;
    }

    // #endregion

    // #region AssetScalar Removal Helpers

    private removeAssetScalar(target: cc.Node): boolean
    {
        let scaler: AssetScaler = target.getComponent(AssetScaler);

        if (scaler)
        {
            this.warn(target, `Found ${AssetScaler.name}.`);
            if (this.shouldModify)
            {
                target.removeComponent(AssetScaler);
                this.warn(target, `FIXED - ${AssetScaler.name} removed.`);
            }
            return true;
        }

        return false;
    }

    // #endregion

}
